import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "../../components";
import * as ROUTES from "../../constants/routes";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../components/dropdown/Dropdown";
import { capitalize } from "@sellar-io/common";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import { Delivery } from "./Delivery";
import {
  Button,
  Card,
  DataButton,
  H5,
  IconType,
  ListItemIconTile,
} from "@sellar-io/components";
import { DeliveryAreaListResponseType } from "@sellar-io/api-v2/types";
import { useDeliveryAreaList } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaList";
import { CourierScheduleCreateDialog } from "../deliveryCharges/modals/CourierScheduleCreateDialog";
import { CourierScheduleUpdateDialog } from "../deliveryCharges/modals/CourierScheduleUpdateDialog";
import { useCourierDeliveryScheduleList } from "../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleList";
import { DeliveryScheduleDeleteDialog } from "./components/DeliveryScheduleDeleteDialog";
import { Alert } from "../../components/alert/Alert";
import { useDeliveryZoneMode } from "../../hooks/useDeliveryZoneMode";

const sorter = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

export const DeliveryScheduleList = () => {
  const navigate = useNavigate();
  const deliveryZoneMode = useDeliveryZoneMode();

  const [courierScheduleActive, setCourierScheduleActive] = useState(false);
  const [updateCourierScheduleActive, setUpdateCourierScheduleActive] =
    useState(false);

  const [deleteDialogActive, setDeleteDialogActive] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<{
    id: number;
    name: string;
  }>();

  const deliveryAreaQuery = useDeliveryAreaList({
    limit: 10,
    sortBy: "createdAt",
    sortDir: "desc",
  });

  const flatLists = useMemo(
    () => deliveryAreaQuery.data?.pages.flatMap((a) => a) ?? [],
    [deliveryAreaQuery.data],
  );

  const courierDeliveryScheduleQuery = useCourierDeliveryScheduleList({
    query: [],
    sortBy: "createdAt",
    sortDir: "desc",
  });

  const courierDeliverySchedule =
    courierDeliveryScheduleQuery.data?.pages[0]?.[0] ?? null;

  const getSubtitle = (
    deliverySchedule: DeliveryAreaListResponseType[number],
  ) => {
    return !!deliverySchedule.DeliveryAreaDays.length ? (
      <>
        Every{" "}
        {deliverySchedule.weeklyCadence === 1
          ? "week"
          : `${deliverySchedule.weeklyCadence} weeks`}{" "}
        on{" "}
        {deliverySchedule.DeliveryAreaDays.sort(
          (a, b) => sorter[a.day] - sorter[b.day],
        )
          .map((deliveryAreaDay) =>
            capitalize(deliveryAreaDay.day.substring(0, 3)),
          )
          .join(" & ")}
      </>
    ) : (
      "Ad-hoc delivery schedule"
    );
  };

  const courierSubtitle = () => {
    if (
      !courierDeliverySchedule ||
      courierDeliverySchedule.CourierDeliveryScheduleDay.length === 0
    ) {
      return "Not specified";
    }

    const days = courierDeliverySchedule.CourierDeliveryScheduleDay.map(
      (day) => `${capitalize(day.day)}s`,
    ).join(", ");
    return `Shipping on ${days}`;
  };

  return (
    <Delivery>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-1">
              <H5>Direct delivery schedules</H5>
              <span className="text-zinc-500">
                Describe where you go and when on your direct delivery runs
              </span>
            </div>
            <div className="flex items-center gap-2">
              <DataButton
                className="flex items-center gap-1"
                border={true}
                onClick={() =>
                  navigate(ROUTES.DELIVERY_SCHEDULE_NEW, {
                    state: {
                      redirectToNewDeliverySchedule: true,
                    },
                  })
                }
              >
                <Icon icon="PLUS" />
                New
              </DataButton>
            </div>
          </div>

          {deliveryZoneMode.isBasicMode && (
            <Alert
              type="information"
              message={
                <strong>
                  The postcodes listed in your direct delivery schedules make up
                  your Local default delivery zone
                </strong>
              }
              className="w-full px-3 py-1"
            />
          )}

          <InfiniteList
            query={deliveryAreaQuery}
            className="mb-8 flex flex-col gap-2"
          >
            {flatLists.map((deliverySchedule, idx) => (
              <ListItem
                key={idx}
                name={deliverySchedule.name}
                subtitle={getSubtitle(deliverySchedule)}
                icon="MAP_CLOCK"
                onClick={() =>
                  navigate(
                    ROUTES.DELIVERY_SCHEDULE.href({
                      deliveryAreaId: deliverySchedule.id,
                    }),
                    {
                      state: {
                        redirectToNewDeliverySchedule: true,
                      },
                    },
                  )
                }
                rightComponent={
                  <Dropdown>
                    <DropdownButton>
                      <Button size={"small"} color={"transparent"}>
                        <Icon
                          icon={"PENCIL"}
                          className="text-black"
                          size={0.7}
                        />
                      </Button>
                    </DropdownButton>
                    <DropdownItems>
                      <DropdownItem
                        className="border-b border-zinc-200 text-sm"
                        onClick={() =>
                          navigate(
                            ROUTES.DELIVERY_SCHEDULE.href({
                              deliveryAreaId: deliverySchedule.id,
                            }),
                            {
                              state: {
                                redirectToNewDeliverySchedule: true,
                              },
                            },
                          )
                        }
                      >
                        Edit
                      </DropdownItem>
                      <DropdownItem
                        className="border-b border-zinc-200 text-sm text-red-700"
                        onClick={() => {
                          setSelectedSchedule({
                            id: deliverySchedule.id,
                            name: deliverySchedule.name,
                          });
                          setDeleteDialogActive(true);
                        }}
                      >
                        Delete
                      </DropdownItem>
                    </DropdownItems>
                  </Dropdown>
                }
              />
            ))}
          </InfiniteList>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-1">
              <H5>Third party shipping schedules</H5>
              <span className="text-zinc-500">
                Give your customers an accurate delivery date prediction for
                orders fulfilled by third party
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            {[
              {
                name: "Courier shipping schedule",
                onClick: () => {
                  if (courierDeliverySchedule) {
                    setUpdateCourierScheduleActive(true);
                  } else {
                    setCourierScheduleActive(true);
                  }
                },
                subtitle: courierSubtitle(),
                active: true,
              },
              {
                name: "Pallet shipping schedule",
                onClick: () => console.log("click"),
                subtitle: "Not specified",
                active: false,
              },
            ]
              .filter((thirdParty) => thirdParty.active)
              .map((thirdParty, idx) => (
                <ListItem
                  key={`thirdPartySchedule-${idx}`}
                  name={thirdParty.name}
                  subtitle={thirdParty.subtitle}
                  icon="CALENDAR_CLOCK"
                  onClick={thirdParty.onClick}
                  rightComponent={
                    <Dropdown>
                      <DropdownButton>
                        <Button size={"small"} color={"transparent"}>
                          <Icon
                            icon={"PENCIL"}
                            className="text-black"
                            size={0.7}
                          />
                        </Button>
                      </DropdownButton>
                      <DropdownItems>
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm"
                          onClick={thirdParty.onClick}
                        >
                          Edit
                        </DropdownItem>
                      </DropdownItems>
                    </Dropdown>
                  }
                />
              ))}
          </div>
        </div>
      </div>
      <CourierScheduleCreateDialog
        active={courierScheduleActive}
        onDismiss={() => setCourierScheduleActive(false)}
      />
      {courierDeliverySchedule && (
        <CourierScheduleUpdateDialog
          active={updateCourierScheduleActive}
          onDismiss={() => setUpdateCourierScheduleActive(false)}
          courierDeliverySchedule={courierDeliverySchedule}
        />
      )}
      {selectedSchedule && (
        <DeliveryScheduleDeleteDialog
          active={deleteDialogActive}
          setActive={setDeleteDialogActive}
          deliveryScheduleId={selectedSchedule.id}
          deliveryScheduleName={selectedSchedule.name}
        />
      )}
    </Delivery>
  );
};

const ListItem = ({
  name,
  subtitle,
  onClick,
  rightComponent,
  icon,
}: {
  name: string;
  subtitle: ReactNode;
  onClick: () => void;
  rightComponent?: ReactNode;
  icon: IconType;
}) => {
  return (
    <Card
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
    >
      <ListItemIconTile icon={icon} />
      <div className="flex w-full items-center gap-4 md:flex-nowrap">
        <div className="flex flex-col">
          <p className="font-semibold">{name}</p>
          <p className="text-sm text-zinc-700">{subtitle}</p>
        </div>
        <span className="ml-auto flex flex-none items-center gap-2">
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => e.stopPropagation()}
          >
            {rightComponent ?? <Icon icon="CHEVRON_RIGHT" size={1} />}
          </div>
        </span>
      </div>
    </Card>
  );
};
